import React from "react";
import { Bar, BarChart, Label, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const CustomerBucketCountGraph = ({ graphData }) => {
    // Early return if no data
    if (!graphData || graphData.length === 0) {
        return (
            <div style={{ 
                width: '100%', 
                height: 200, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#666',
                fontSize: '14px'
            }}>
                No data available
            </div>
        );
    }

    const CustomLegend = () => {
        const items = [
            { label: 'Suggested Ordered', color: '#E8E190' },
            { label: 'Not Suggested Not Ordered', color: '#98FF98' },
            { label: 'Not Suggested Ordered', color: '#91b8db' },
            { label: 'Suggested Not Ordered', color: '#FA8072' },
        ];

        return (
            <div style={{ display: 'flex', gap: 16, fontSize: 12, justifyContent: 'center', marginTop: 10 }}>
                {items.map(item => (
                    <div
                        key={item.label}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: 4,
                                backgroundColor: item.color,
                                display: 'inline-block',
                            }}
                        />
                        <span>{item.label}</span>
                    </div>
                ))}
            </div>
        );
    };

    const CustomBarLabel = ({ x, y, width, value }) => {
        return (
            <text
                x={x + width - 3}
                y={y + 11}
                fill="#333"
                textAnchor="end"
                fontSize={10}
                fontWeight={600}
            >
                {value}
            </text>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        minWidth: '200px',
                    }}
                >
                    <p
                        style={{
                            margin: '0 0 8px 0',
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#333',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '6px',
                        }}
                    >
                        {label}
                    </p>
                    {payload.map((entry, index) => (
                        <div
                            key={`item-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                margin: '6px 0',
                                fontSize: '11px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span
                                    style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '2px',
                                        backgroundColor: entry.color,
                                        display: 'inline-block',
                                    }}
                                />
                                <span style={{ color: '#666' }}>{entry.name}:</span>
                            </div>
                            <span style={{ fontWeight: 600, color: '#333', marginLeft: '8px' }}>
                                {entry.value}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const barGroupHeight = 60; // Height per bar group (includes all bars + spacing)
    const legendHeight = 50; // Space for legend at bottom
    const minHeight = 600; // Minimum height to fill parent container
    const maxHeight = 700; // Maximum visible height before scroll kicks in
    
    // Calculate actual height needed based on data
    const calculatedHeight = (graphData?.length || 0) * barGroupHeight + legendHeight;
    
    // Determine if scrolling is needed
    const needsScroll = calculatedHeight > maxHeight;
    
    // Use minHeight if calculated is less, otherwise use calculated (up to maxHeight for container)
    const chartHeight = Math.max(minHeight, calculatedHeight);
    const containerHeight = needsScroll ? maxHeight : chartHeight;

    return (
        <div style={{
            width: '100%',
            height: containerHeight,
        }}>
            <div style={{ width: '100%', height: needsScroll ? calculatedHeight : '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={graphData}
                        layout="vertical"
                        barGap={0}
                        barCategoryGap={30}
                        margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
                    >
                        <XAxis
                            type="number"
                            domain={[0, 'auto']}
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={true}
                        />
                        <YAxis
                            dataKey="customer_name"
                            type="category"
                            width={70}
                            tick={{ fontSize: 10, fontWeight: 600 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />

                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            content={<CustomLegend />}
                        />
                        <Bar dataKey="Suggested Ordered" fill="#E8E190" radius={[0, 6, 6, 0]} barSize={18} >
                            <LabelList content={<CustomBarLabel />} />
                        </Bar>
                        <Bar dataKey="Not Suggested Not Ordered" fill="#98FF98" radius={[0, 6, 6, 0]} barSize={18} >
                            <LabelList content={<CustomBarLabel />} />
                        </Bar>
                        <Bar dataKey="Not Suggested Ordered" fill="#91b8db" radius={[0, 6, 6, 0]} barSize={18} >
                            <LabelList content={<CustomBarLabel />} />
                        </Bar>
                        <Bar dataKey="Suggested Not Ordered" fill="#FA8072" radius={[0, 6, 6, 0]} barSize={18} >
                            <LabelList content={<CustomBarLabel />} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div >
    );
};

export default CustomerBucketCountGraph;